import axios from 'axios';
import {
  CREATE_ENTITY_TYPE_URL,
  LIST_ENTITY_PROFILES_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';
import {
  getXVerdictFullConfigByEntityType,
  listXVerdictEntityConfigs,
} from '@/services/xverdict_entity_config_service';
import {
  mapEntityConfigToEntityType,
} from '@/services/xverdict_config_mapper';
import {
  normalize_entity_type_page,
  normalize_text,
  parse_response_data,
  to_array,
  to_number
} from './case_domains_common';
import { count_fields, map_domain } from './case_domains_mapping';

const ENTITY_TYPE_PAGE_SIZE = 500;

function project_params(project_id, extra = {}) {
  return {
    ...(project_id ? { project_id } : {}),
    ...extra
  };
}

function entity_types_from_config_rows(config_rows = []) {
  return config_rows.map(mapEntityConfigToEntityType).filter(Boolean);
}

function entity_types_from_legacy_response(parsed) {
  return to_array(parsed?.data || parsed).filter((item) => to_number(item?.id));
}

function collect_entity_type_pages(first_page, remaining_pages = []) {
  return [
    ...first_page.items,
    ...remaining_pages.flatMap((page) => page.items)
  ];
}

function total_entity_type_pages(first_page, page_size) {
  return first_page.total_pages || Math.ceil(first_page.count / page_size) || 1;
}

function domain_form_values(form = {}) {
  return {
    entity_type_name: normalize_text(form.entity_type_name),
    entity_type_description: normalize_text(form.entity_type_description)
  };
}

function validate_domain_form(component, values = {}) {
  if (values.entity_type_name) return true;

  component.add_domain_error = 'Domain name is required.';
  return false;
}

function domain_create_payload(values = {}) {
  return {
    entity_type_name: values.entity_type_name,
    entity_type_description: values.entity_type_description
  };
}

async function submit_domain_create(payload = {}) {
  const response = await axios.post(
    CREATE_ENTITY_TYPE_URL,
    payload,
    get_request_config()
  );
  return parse_response_data(response);
}

function assert_domain_create_success(parsed = {}) {
  if (parsed?.status && String(parsed.status).toLowerCase() !== 'success') {
    throw new Error(parsed?.message || 'Could not add domain.');
  }
}

async function handle_domain_create_success(component) {
  component.show_add_domain_modal = false;
  component.reset_add_domain_form();
  await component.load_domains();
}

function handle_domain_create_error(component, error) {
  const parsed = parse_response_data(error?.response?.data || {});
  component.add_domain_error = domain_create_error_message(parsed, error);
}

function domain_create_error_message(parsed = {}, error = {}) {
  return first_domain_create_error([parsed?.message, parsed?.errors, error?.message]);
}

function first_domain_create_error(values = []) {
  return values.find(Boolean) || 'Could not add domain.';
}

export default {
  name: 'CaseDomains',
  props: {
    projectId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    loading_domains: false,
    creating_domain: false,
    show_add_domain_modal: false,
    domains_error: '',
    domains: [],
    add_domain_form: {
      entity_type_name: '',
      entity_type_description: ''
    },
    add_domain_error: ''
  }),
  mounted() {
    this.load_domains();
  },
  methods: {
    request_params(extra = {}) {
      return project_params(this.projectId, extra);
    },
    async fetch_entity_type_page(page = 1, page_size = 500) {
      const params = this.request_params({ page, page_size });
      const response = await axios.get(
        VIEW_ENTITY_TYPE_URL,
        get_request_config({ params })
      );
      const parsed = parse_response_data(response);
      return normalize_entity_type_page(parsed, page);
    },
    async fetch_entity_types() {
      const entity_types = await this.fetch_config_entity_types();

      if (entity_types.length) {
        return entity_types;
      }

      try {
        return await this.fetch_paged_entity_types();
      } catch {
        return this.fetch_legacy_entity_types();
      }
    },
    async fetch_config_entity_types() {
      const config_rows = await listXVerdictEntityConfigs(this.request_params()).catch(() => []);
      return entity_types_from_config_rows(config_rows);
    },
    async fetch_paged_entity_types() {
      const first_page = await this.fetch_entity_type_page(1, ENTITY_TYPE_PAGE_SIZE);
      const total_pages = total_entity_type_pages(first_page, ENTITY_TYPE_PAGE_SIZE);

      if (total_pages <= 1) {
        return first_page.items;
      }

      const remaining_pages = await Promise.all(
        Array.from({ length: total_pages - 1 }, (_, index) =>
          this.fetch_entity_type_page(index + 2, ENTITY_TYPE_PAGE_SIZE)
        )
      );

      return collect_entity_type_pages(first_page, remaining_pages);
    },
    async fetch_legacy_entity_types() {
      const response = await axios.get(
        VIEW_ENTITY_TYPE_URL,
        get_request_config({ params: this.request_params() })
      );
      const parsed = parse_response_data(response);
      return entity_types_from_legacy_response(parsed);
    },
    async fetch_full_config(entity_type_id) {
      return getXVerdictFullConfigByEntityType({
        entity_type_id,
        ...(this.projectId ? { project_id: this.projectId } : {}),
      });
    },
    async fetch_profiles(entity_type_id) {
      const response = await axios.get(
        LIST_ENTITY_PROFILES_URL,
        get_request_config({
          params: this.request_params({
            entity_type_id,
            page: 1,
            page_size: 500
          })
        })
      );
      const parsed = parse_response_data(response);
      return to_array(parsed?.data || parsed);
    },
    count_fields(config_data = {}) {
      return count_fields(config_data);
    },
    map_domain(entity_type, index, config_data = {}, profiles = []) {
      return map_domain(entity_type, index, config_data, profiles);
    },
    async enrich_domain(entity_type, index) {
      const entity_type_id = to_number(entity_type?.id);
      const [config_result, profiles_result] = await Promise.allSettled([
        this.fetch_full_config(entity_type_id),
        this.fetch_profiles(entity_type_id)
      ]);

      return this.map_domain(
        entity_type,
        index,
        config_result.status === 'fulfilled' ? config_result.value : {},
        profiles_result.status === 'fulfilled' ? profiles_result.value : []
      );
    },
    start_domains_loading() {
      this.loading_domains = true;
      this.domains_error = '';
    },
    async mapped_domains(entity_types = []) {
      return Promise.all(
        entity_types.map((entity_type, index) => this.enrich_domain(entity_type, index))
      );
    },
    apply_domains_error(error) {
      this.domains = [];
      this.domains_error = error?.message || 'Could not load case domains.';
    },
    finish_domains_loading() {
      this.loading_domains = false;
    },
    async load_domains() {
      this.start_domains_loading();

      try {
        this.domains = await this.mapped_domains(await this.fetch_entity_types());
      } catch (error) {
        this.apply_domains_error(error);
      } finally {
        this.finish_domains_loading();
      }
    },
    reset_add_domain_form() {
      this.add_domain_form = {
        entity_type_name: '',
        entity_type_description: ''
      };
      this.add_domain_error = '';
    },
    go_to_add_domain() {
      this.reset_add_domain_form();
      this.show_add_domain_modal = true;
    },
    close_add_domain_modal() {
      if (this.creating_domain) {
        return;
      }

      this.show_add_domain_modal = false;
      this.reset_add_domain_form();
    },
    async create_domain() {
      this.add_domain_error = '';
      const values = domain_form_values(this.add_domain_form);

      if (!validate_domain_form(this, values)) {
        return;
      }

      this.creating_domain = true;

      try {
        const parsed = await submit_domain_create(domain_create_payload(values));
        assert_domain_create_success(parsed);
        await handle_domain_create_success(this);
      } catch (error) {
        handle_domain_create_error(this, error);
      } finally {
        this.creating_domain = false;
      }
    }
  }
};
